import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiRequest } from "src/services/apiClient";
import { Section } from "./SettingsControls";
import { Doctor } from "src/types";
import { PasswordInput } from "src/components/PasswordInput/PasswordInput";
import { useEscKey } from "src/hooks/useEscKey";
import { useFocusTrap } from "src/hooks/useFocusTrap";

interface Props {
    doctors: Doctor[] | null;
    currentDoctorId: string;
}

export function ResetMemberPasswordSection({ doctors, currentDoctorId }: Props) {
    const { t } = useTranslation();
    const [targetId, setTargetId]   = useState<string | null>(null);
    const [pw, setPw]               = useState("");
    const [confirm, setConfirm]     = useState("");
    const [saving, setSaving]       = useState(false);
    const [error, setError]         = useState<string | null>(null);
    const [savedId, setSavedId]     = useState<string | null>(null);
    const dialogRef = useRef<HTMLDivElement>(null);
    const titleId = useRef(`reset-pw-title-${Math.random().toString(36).slice(2)}`).current;

    const open = (id: string) => {
        setTargetId(id); setPw(""); setConfirm(""); setError(null);
    };
    const close = () => { setTargetId(null); setPw(""); setConfirm(""); setError(null); };

    useEscKey(close, !!targetId);
    useFocusTrap(!!targetId, dialogRef);

    const handleReset = async () => {
        setError(null);
        if (!pw || !confirm) { setError(t("settings.pwAllFieldsRequired")); return; }
        if (pw.length < 6)   { setError(t("settings.pwTooShort")); return; }
        if (pw !== confirm)  { setError(t("settings.pwMismatch")); return; }
        setSaving(true);
        try {
            await apiRequest(
                `/settings/reset-member-password/${targetId}`,
                { method: "PATCH", body: JSON.stringify({ newPassword: pw }) },
                { "Content-Type": "application/json" },
            );
            setSavedId(targetId);
            setTimeout(() => setSavedId(null), 3000);
            close();
        } catch (e: any) {
            setError(e?.message ?? t("settings.pwError"));
        } finally {
            setSaving(false);
        }
    };

    const members = doctors?.filter((d) => d.id !== currentDoctorId) ?? [];

    if (!members.length) return null;

    return (
        <>
            <Section title={t("settings.resetMemberPasswords")} description={t("settings.resetMemberPasswordsDesc")}>
                <table className="dashboard-table">
                    <tbody>
                        {members.map((doc) => (
                            <tr key={doc.id}>
                                <td>{doc.firstName} {doc.lastName}</td>
                                <td style={{ color: "#9ca3af", fontSize: 13 }}>{doc.email}</td>
                                <td className="team-actions">
                                    {savedId === doc.id ? (
                                        <span className="chip chip--success">{t("settings.saved")}</span>
                                    ) : (
                                        <button className="tbl-btn" onClick={() => open(doc.id)}>
                                            {t("settings.resetPassword")}
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>

            {targetId && (
                <div className="create-modal-overlay" role="presentation" onClick={close}>
                    <div
                        ref={dialogRef}
                        className="create-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={titleId}
                        tabIndex={-1}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="create-modal__title" id={titleId}>
                            {t("settings.resetPasswordFor", {
                                name: doctors?.find((d) => d.id === targetId)
                                    ? `${doctors.find((d) => d.id === targetId)!.firstName} ${doctors.find((d) => d.id === targetId)!.lastName}`
                                    : "",
                            })}
                        </h2>
                        <div className="create-modal__form">
                            <div className="create-modal__field">
                                <label>{t("settings.newPassword")}</label>
                                <PasswordInput
                                    autoComplete="new-password"
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                />
                            </div>
                            <div className="create-modal__field">
                                <label>{t("settings.confirmPassword")}</label>
                                <PasswordInput
                                    autoComplete="new-password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                />
                            </div>
                            {error && <p className="create-modal__error">{error}</p>}
                            <div className="create-modal__actions">
                                <button type="button" className="create-modal__btn create-modal__btn--cancel" onClick={close}>
                                    {t("form.cancel")}
                                </button>
                                <button
                                    type="button"
                                    className="create-modal__btn create-modal__btn--submit"
                                    disabled={saving}
                                    onClick={handleReset}
                                >
                                    {saving ? t("settings.saving") : t("settings.resetPasswordBtn")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
